import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { sequenceTOption } from '../function';

interface TimestampedItem {
  timestamp: Date;
}

export const getIntervalSlice = <T extends TimestampedItem>(
  points: T[],
  slice: number,
  intervalLengthMins = 10,
) => {
  const startTime = pipe(
    A.head(points),
    O.map((p) => p.timestamp),
    O.map((t) => addMinutes(t, slice * intervalLengthMins)),
  );

  const endTime = pipe(
    startTime,
    O.map((t) => addMinutes(t, intervalLengthMins)),
  );

  return pipe(
    sequenceTOption(startTime, endTime),
    O.map(([startTime, endTime]) =>
      points.filter(
        ({ timestamp }) => timestamp >= startTime && timestamp < endTime,
      ),
    ),
    O.getOrElse<T[]>(() => []),
  );
};

export const getSliceCount = (points: TimestampedItem[]) => {
  return pipe(
    sequenceTOption(A.head(points), A.last(points)),
    O.map(
      ([{ timestamp: startTime }, { timestamp: endTime }]) =>
        [startTime, endTime] as const,
    ),
    O.map(([startTime, endTime]) => {
      const mins = differenceInMinutes(endTime, startTime);
      return Math.floor(mins / 10) + 1;
    }),
    O.getOrElse(() => 1),
  );
};
